import { useCallback, useEffect, useRef, useState } from 'react';
import { maxCornerDisplacement } from '../utils/geometry';

export function useAutoCapture(corners, areaRatio, found, options = {}) {
  const {
    stabilityThreshold = 10,
    movementTolerance = 8,
    minAreaRatio = 0.2,
    enabled = true,
  } = options;

  const prevCornersRef = useRef(null);
  const stableCountRef = useRef(0);
  const captureCallbackRef = useRef(null);
  const [status, setStatus] = useState('no-card');
  const [stableFrames, setStableFrames] = useState(0);

  const onCaptureTrigger = useCallback((callback) => {
    captureCallbackRef.current = callback;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (!found || !corners) {
      stableCountRef.current = 0;
      prevCornersRef.current = null;
      setStatus('no-card');
      setStableFrames(0);
      return;
    }

    if (areaRatio < minAreaRatio) {
      stableCountRef.current = 0;
      setStatus('card-detected');
      setStableFrames(0);
      prevCornersRef.current = corners;
      return;
    }

    if (prevCornersRef.current) {
      const displacement = maxCornerDisplacement(prevCornersRef.current, corners);

      if (displacement < movementTolerance) {
        stableCountRef.current++;
      } else {
        stableCountRef.current = 0;
      }
    }

    prevCornersRef.current = corners;
    setStableFrames(stableCountRef.current);

    if (stableCountRef.current >= stabilityThreshold) {
      setStatus('capturing');
      if (captureCallbackRef.current) {
        captureCallbackRef.current();
        captureCallbackRef.current = null;
      }
    } else if (stableCountRef.current > 0) {
      setStatus('stable');
    } else {
      setStatus('card-detected');
    }
  }, [corners, areaRatio, found, enabled, stabilityThreshold, movementTolerance, minAreaRatio]);

  return { status, stableFrames, onCaptureTrigger };
}
